import { useEffect, useRef, useState } from 'react';
import { supportsIntersectionObserver } from '@/utils/polyfills';

interface UseIntersectionObserverOptions {
  threshold?: number;
  rootMargin?: string;
  root?: Element | null;
  /** Si true, marque comme visible immédiatement si IntersectionObserver n'est pas supporté */
  fallbackVisible?: boolean;
}

export const useIntersectionObserver = (
  options: UseIntersectionObserverOptions = {}
): [React.RefObject<HTMLDivElement>, boolean] => {
  const { 
    threshold = 0.1, 
    rootMargin = '0px', 
    root = null,
    fallbackVisible = true 
  } = options;
  
  // Fallback: si IntersectionObserver n'est pas supporté, considérer comme visible
  const [isVisible, setIsVisible] = useState(!supportsIntersectionObserver && fallbackVisible);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Si IntersectionObserver n'est pas supporté, utiliser le fallback
    if (!supportsIntersectionObserver) {
      if (fallbackVisible) {
        setIsVisible(true);
      }
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold,
        rootMargin,
        root
      }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold, rootMargin, root, fallbackVisible]);

  return [ref, isVisible];
};
